import React, { useEffect, useMemo, useRef, useState, useCallback, useLayoutEffect } from 'react';
import { Box, Typography, CircularProgress, Checkbox } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useConciergeStore } from '../../store/conciergeStore';

import ConciergeBubble from './ConciergeBubble';

// --- role helper (supports legacy string or structured sender) ---
function roleOf(sender) {
  if (!sender) return undefined;
  if (typeof sender === 'string') return sender === 'guest' ? 'guest' : 'staff';
  if (typeof sender === 'object') return sender.role || undefined;
  return undefined;
}

// Normalize messages to always carry sender.role ('staff' | 'guest') and keep legacy flag
function withNormalizedSender(m) {
  if (!m) return m;
  // Resolve role from various legacy shapes
  const role =
    (m?.sender && typeof m.sender === 'object' && (m.sender.role === 'guest' ? 'guest' : (m.sender.role === 'staff' ? 'staff' : undefined))) ||
    (typeof m?.sender === 'string' && (m.sender === 'guest' ? 'guest' : (m.sender === 'staff' ? 'staff' : undefined))) ||
    (typeof m?.fromGuest === 'boolean' && (m.fromGuest ? 'guest' : 'staff')) ||
    (m?.role === 'guest' ? 'guest' : (m?.role === 'staff' ? 'staff' : undefined));

  if (!role) return m;
  return {
    ...m,
    sender: { role },
    fromGuest: role === 'guest',
  };
}

// Use the composite key the store uses: `${propertyId}:${userUid}`
const toIdString = (v) => {
  if (v && typeof v === 'object') {
    if (v._id) return String(v._id);
    if (v.$oid) return String(v.$oid);
  }
  return String(v || '');
};

/**
 * ConciergeMessageList
 * Scrollable message list for the concierge chat room with auto-scroll-to-bottom
 * and lazy loading (fetch older) on reaching the top.
 *
 * Props:
 *  - propertyId: string (required)
 *  - thread: { id?, propertyId, userUid } (required)
 *  - messages: array (optional)
 *  - threadKey: string (optional)
 *  - height: CSS height (default: '100%')
 */
export default function ConciergeMessageList({ propertyId, thread, messages: propMessages, threadKey: propThreadKey, height = '100%' }) {
  const {
    isSelectionMode,
    isSelected,
    toggleSelectMessage,
    clearSelection,
    getSelection,
  } = useConciergeStore();

  const {
    messagesByThread,
    fetchHistory,
    loading,
    endOfHistoryByThread,
  } = useConciergeStore();

  const effectiveThreadKey = useMemo(() => (
    propThreadKey || (thread && thread.propertyId && thread.userUid
      ? `${toIdString(thread.propertyId)}:${thread.userUid}`
      : null)
  ), [propThreadKey, thread]);

  const isEnd = useMemo(() => !!(effectiveThreadKey && endOfHistoryByThread && endOfHistoryByThread[effectiveThreadKey]), [endOfHistoryByThread, effectiveThreadKey]);

  const selectionActive = isSelectionMode?.(propertyId, thread?.userUid);

  const threadKey = useMemo(() => (
    propertyId && thread?.userUid ? `${toIdString(propertyId)}:${thread.userUid}` : null
  ), [propertyId, thread]);

  const selectedIds = useMemo(() => (
    getSelection?.(propertyId, thread?.userUid) || []
  ), [getSelection, propertyId, thread]);

  useEffect(() => {
    console.log('[ConciergeMessageList] selection snapshot', {
      threadKey,
      selectionActive,
      selectedCount: selectedIds.length,
      selectedIds,
    });
  }, [threadKey, selectionActive, selectedIds]);

  const messages = useMemo(() => (propMessages ?? (effectiveThreadKey ? (messagesByThread[effectiveThreadKey] || []) : [])), [propMessages, effectiveThreadKey, messagesByThread]);

  const sortedMessages = useMemo(() => {
    if (!messages || messages.length === 0) return messages;
    // Sort ascending so the newest message is at the bottom of the list
    return [...messages].sort((a, b) => {
      const ta = new Date(a.timestamp || a.createdAt || 0).getTime();
      const tb = new Date(b.timestamp || b.createdAt || 0).getTime();
      return ta - tb;
    });
  }, [messages]);

  const enrichedMessages = useMemo(() => {
    if (!sortedMessages || sortedMessages.length === 0) return sortedMessages || [];
    // Build an id -> message map for quick lookup
    const byId = Object.create(null);
    for (const m of sortedMessages) {
      const mid = m?.id || m?._id || m?.tempId;
      if (mid) byId[String(mid)] = m;
    }
    // For each message, if it has replyTo but no replySnapshot, derive a minimal snapshot
    return sortedMessages.map((m) => {
      if (!m?.replyTo) return m;
      if (m?.replySnapshot) return m; // keep server-provided snapshot
      const parent = byId[String(m.replyTo)];
      if (!parent) return m; // parent not in memory (older page)
      const parentRole = roleOf(parent?.sender);
      const parentDisplay =
        (typeof parent?.sender === 'object' && (parent?.sender?.displayName || parent?.sender?.name)) ||
        (parentRole === 'guest' ? 'Guest' : (parentRole === 'staff' ? 'Staff' : (parent?.user?.name || 'Message')));
      const snap = {
        text: parent?.text || parent?.content || (parent?.attachments?.length ? '(attachment)' : ''),
        sender: parentDisplay,
        createdAt: parent?.createdAt || parent?.timestamp,
      };
      return { ...m, replySnapshot: snap };
    });
  }, [sortedMessages]);

  // Highlight + scroll-to-parent support (reply preview tap)
  const [highlightedId, setHighlightedId] = useState(null);
  const lastLoadedPageRef = useRef(1);

  const handleQuotedClick = useCallback((parentId) => {
    if (!parentId || !scrollRef.current) return;
    const tryScroll = () => {
      const el = scrollRef.current?.querySelector?.(`[data-msg-id="${parentId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightedId(String(parentId));
        window.setTimeout(() => setHighlightedId(null), 1400);
        return true;
      }
      return false;
    };
    // First attempt with current DOM
    if (tryScroll()) return;
    // Not found: best-effort fetch one more page and retry once
    if (propertyId && thread?.userUid && typeof fetchHistory === 'function') {
      const nextPage = (lastLoadedPageRef.current || 1) + 1;
      fetchHistory(propertyId, thread.userUid, { page: nextPage, limit: 50 })
        .then(() => {
          lastLoadedPageRef.current = nextPage;
          requestAnimationFrame(() => { tryScroll(); });
        })
        .catch(() => {});
    }
  }, [propertyId, thread, fetchHistory]);

  const lastMsgIdRef = useRef(null);
  const stickToBottomRef = useRef(true);

  const scrollRef = useRef(null);
  const [page, setPage] = useState(1);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  // Initial fetch when thread changes
  useEffect(() => {
    if (!propertyId || !thread) return;
    // Debug: log initial fetchHistory
    setPage(1);
    fetchHistory(propertyId, thread.userUid, { page: 1, limit: 50 }).catch((e) =>
      console.warn('[ConciergeMessageList] initial fetchHistory error', e)
    );
  }, [propertyId, thread, fetchHistory]);

  // On thread change: jump to bottom once and attach passive scroll listener
  useEffect(() => {
    if (!scrollRef.current) return;
    const el = scrollRef.current;
    el.scrollTop = el.scrollHeight;
    const onScrollPassive = () => {
      const distanceFromBottom = el.scrollHeight - el.clientHeight - el.scrollTop;
      stickToBottomRef.current = distanceFromBottom < 100; // within 100px
    };
    el.addEventListener('scroll', onScrollPassive, { passive: true });
    return () => el.removeEventListener('scroll', onScrollPassive);
  }, [effectiveThreadKey]);

  // When messages change, scroll to bottom only if a new last message arrived and user is near bottom
  useEffect(() => {
    if (!scrollRef.current || !sortedMessages || sortedMessages.length === 0) return;
    const last = sortedMessages[sortedMessages.length - 1];
    const lastId = last && (last.id || last._id || last.tempId);
    const isNewLast = lastId && lastMsgIdRef.current !== lastId;
    if (isNewLast && stickToBottomRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'auto' });
    }
    if (isNewLast) lastMsgIdRef.current = lastId;
  }, [sortedMessages]);

  useLayoutEffect(() => {
    // Inject a light highlight style once (scoped enough)
    const id = 'concierge-highlight-style';
    if (document.getElementById(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.textContent = `
      .msg-highlight {
        background-color: #FFF7CC;
        transition: background-color 1s ease;
        border-radius: 6px;
      }
    `;
    document.head.appendChild(style);
    return () => {
      // keep it around; no cleanup needed across mounts in a SPA
    };
  }, []);

  // Fetch older on reach top
  const onScroll = useCallback(async (e) => {
    const el = e.currentTarget;
    const distanceFromBottom = el.scrollHeight - el.clientHeight - el.scrollTop;
    stickToBottomRef.current = distanceFromBottom < 100;
    // Debug: log scroll event
    if (!el || isFetchingMore || loading) return;
    if (el.scrollTop <= 0 && propertyId && thread && !isEnd) {
      try {
        setIsFetchingMore(true);
        const nextPage = page + 1;
        const prevHeight = el.scrollHeight;
        await fetchHistory(propertyId, thread.userUid, { page: nextPage, limit: 50 });
        setPage(nextPage);
        // Maintain scroll position after prepending older messages
        const newHeight = el.scrollHeight;
        el.scrollTop = newHeight - prevHeight;
      } catch (err) {
        console.warn('[ConciergeMessageList] fetch older error', err);
      } finally {
        setIsFetchingMore(false);
      }
    }
  }, [isFetchingMore, loading, page, propertyId, thread, fetchHistory, isEnd]);

  // --- date divider helpers ---
  function msgTimeMs(m) {
    const v = (m?.timestamp ?? m?.createdAt ?? m?.time ?? m?.sentAt);
    const t = Date.parse(String(v));
    return Number.isFinite(t) ? t : undefined;
  }
  function sameDay(aMs, bMs) {
    if (aMs == null || bMs == null) return false;
    const a = new Date(aMs); const b = new Date(bMs);
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }
  function formatDay(ms) {
    if (ms == null) return '';
    const d = new Date(ms);
    const now = new Date();
    const isThisYear = d.getFullYear() === now.getFullYear();
    const opts = isThisYear ? { month: 'long', day: 'numeric' } : { month: 'long', day: 'numeric', year: 'numeric' };
    try { return new Intl.DateTimeFormat(undefined, opts).format(d); } catch { return d.toDateString(); }
  }

  return (
    <Box
      ref={scrollRef}
      onScroll={onScroll}
      sx={{ flex: 1, overflowY: 'auto', p: 2, height, bgcolor: '#ffffffff' }}
      onClick={(e) => {
        if (e.currentTarget === e.target) {
          console.log('[ConciergeMessageList] blank area clicked – clearing selection', { threadKey });
          clearSelection(propertyId, thread?.userUid);
        }
      }}
      onContextMenu={(e) => {
        // Only trigger when user right-clicks on the blank area, not on a message row
        if (!e.isTrusted) return; // ignore synthetic events
        if (e.currentTarget === e.target) {
          e.preventDefault();
          e.stopPropagation();
          const position = { x: e.clientX, y: e.clientY };
          try {
            const open = window.__conciergeContextMenuOpen;
            if (typeof open === 'function') {
              open({
                position,
                message: null,
                msgId: null,
                propertyId,
                userUid: thread?.userUid,
                blankArea: true,
              });
            }
          } catch (_) {}
        }
      }}
    >
      {loading && enrichedMessages.length === 0 ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={22} />
          <Typography variant="body2" sx={{ ml: 1 }} color="text.secondary">Loading…</Typography>
        </Box>
      ) : enrichedMessages.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No messages yet.</Typography>
      ) : (
        <>
          {isEnd && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 1.5 }}>
              <Box sx={{ flex: 1, height: 1, bgcolor: 'divider', opacity: 0.5 }} />
              <Typography variant="caption" sx={{ mx: 1.5, color: 'text.disabled', letterSpacing: 0.3 }}>
                — End of conversation —
              </Typography>
              <Box sx={{ flex: 1, height: 1, bgcolor: 'divider', opacity: 0.5 }} />
            </Box>
          )}
          {isFetchingMore && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 1 }}>
              <CircularProgress size={18} />
            </Box>
          )}
          {enrichedMessages.map((raw, i) => {
            const m = withNormalizedSender(raw);
            const mid = m.id || m._id || m.tempId;
            const isHighlighted = highlightedId && String(highlightedId) === String(mid);
            const selected = isSelected?.(propertyId, thread?.userUid, String(mid));
            const curMs = msgTimeMs(m);
            const prevMs = i > 0 ? msgTimeMs(withNormalizedSender(enrichedMessages[i - 1])) : undefined;
            const needDivider = (curMs != null) && !sameDay(prevMs, curMs);
            return (
              <React.Fragment key={`wrap-${mid}`}>
                {needDivider && (
                  <Box key={`day-${curMs}-${mid}-divider`} sx={{ display: 'flex', justifyContent: 'center', my: 1.25 }}>
                    <Box sx={{
                      px: 1,
                      py: 0.25,
                      borderRadius: 2,
                      fontSize: 12,
                      color: 'text.secondary',
                      bgcolor: 'rgba(0,0,0,0.04)',
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6)'
                    }}>
                      {formatDay(curMs)}
                    </Box>
                  </Box>
                )}

                <Box
                  key={mid}
                  data-msg-id={mid}
                  className={isHighlighted ? 'msg-highlight' : undefined}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    px: 0.5,
                    py: 0.25,
                    borderRadius: 1.5,
                    position: 'relative',
                    transition: 'background-color 120ms ease',
                    ...(selectionActive && selected ? { bgcolor: '#EEF2F7' } : {}),
                    ...(selectionActive && !selected ? { '&:hover': { bgcolor: '#F7F9FB' } } : {}),
                  }}
                  onContextMenu={(e) => {
                    // Enable context menu when user right‑clicks anywhere on the row
                    if (!e || !e.isTrusted) return; // ignore synthetic events to prevent recursion
                    // Don't hijack context menu when selecting via checkbox
                    if ((e.target)?.closest?.('.msg-select-checkbox')) return;
                    e.preventDefault();
                    e.stopPropagation();
                    const position = { x: e.clientX, y: e.clientY };
                    try {
                      const open = window.__conciergeContextMenuOpen;
                      if (typeof open === 'function') {
                        open({
                          position,
                          message: m,
                          msgId: String(mid),
                          propertyId,
                          userUid: thread?.userUid,
                        });
                      }
                    } catch {}
                  }}
                  onClick={(e) => {
                    // Ignore clicks on the checkbox itself
                    if ((e.target)?.closest?.('.msg-select-checkbox')) return;
                    if (selectionActive) {
                      const args = { propertyId, userUid: thread?.userUid, messageId: String(mid) };
                      toggleSelectMessage(args);
                      try {
                        setTimeout(() => {
                          const st = window.__conciergeDebug?.store?.getState?.();
                          if (st && threadKey) {
                            console.log('[ConciergeMessageList] bubble click -> store', {
                              selected: st.selectedByThread?.[threadKey],
                              flag: st.selectionModeByThread?.[threadKey],
                            });
                          }
                        }, 0);
                      } catch {}
                    }
                  }}
                >
                  {selectionActive && (
                    <Box
                      sx={{ position: 'absolute', right: -12, top: '50%', transform: 'translateY(-50%)', zIndex: 2 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        className="msg-select-checkbox"
                        color="primary"
                        checked={!!selected}
                        icon={<RadioButtonUncheckedIcon />}
                        checkedIcon={<CheckCircleIcon />}
                        disableRipple
                        onChange={() => {
                          const args = { propertyId, userUid: thread?.userUid, messageId: String(mid) };
                          toggleSelectMessage(args);
                          try {
                            setTimeout(() => {
                              const st = window.__conciergeDebug?.store?.getState?.();
                              if (st && threadKey) {
                                console.log('[ConciergeMessageList] checkbox toggle -> store', {
                                  selected: st.selectedByThread?.[threadKey],
                                  flag: st.selectionModeByThread?.[threadKey],
                                });
                              }
                            }, 0);
                          } catch {}
                        }}
                        sx={{
                          p: 0.25,
                          '& .MuiSvgIcon-root': {
                            fontSize: 20,
                            borderRadius: '50%',
                            backgroundColor: 'white',
                          },
                        }}
                      />
                    </Box>
                  )}
                  <ConciergeBubble
                    msg={m}
                    propertyId={propertyId}
                    userUid={thread?.userUid}
                    isSelected={!!selected}
                    selectionActive={!!selectionActive}
                    onSelect={(ev, mode = 'auto') => {
                      const args = { propertyId, userUid: thread?.userUid, messageId: String(mid) };
                      console.log('[ConciergeMessageList] onSelect', { threadKey, mode, selectionActive, args });
                      if (mode === 'toggle') {
                        toggleSelectMessage(args);
                      } else if (selectionActive) {
                        toggleSelectMessage(args);
                      } else {
                        // Do nothing when not in selection mode; user must use context menu → Select
                        return;
                      }
                      try {
                        setTimeout(() => {
                          const st = window.__conciergeDebug?.store?.getState?.();
                          if (st && threadKey) {
                            console.log('[ConciergeMessageList] store after select', {
                              selected: st.selectedByThread?.[threadKey],
                              flag: st.selectionModeByThread?.[threadKey],
                            });
                          }
                        }, 0);
                      } catch {}
                    }}
                    onQuotedClick={handleQuotedClick}
                  />
                </Box>
              </React.Fragment>
            );
          })}
        </>
      )}
    </Box>
  );
}